import { createSelectorCreator, defaultMemoize } from 'reselect';
import preloadedState from '../../../store/preloaded-state';
import { requestApi } from '../../../services/api';
import { RootState, ThunkAction } from '../../../store/types';
import type { Translation } from '../types';
import { AnyAction } from 'redux';
import { isEqual } from '@comeon/utility';

type PreloadedState = {
	brand: {
		[key: string]: unknown;
	};
	account: {
		[key: string]: unknown;
	};
	translations?: Translation;
};

export const FETCH_TRANSLATION_START = '@komigen/mobile-frontend/translation/FETCH_TRANSLATION_START' as const;
export const FETCH_TRANSLATION_SUCCESS = '@komigen/mobile-frontend/translation/FETCH_TRANSLATION_SUCCESS' as const;
export const SET_TRANSLATION = '@komigen/mobile-frontend/translation/SET_TRANSLATION' as const;
export const TEXT_KEYS_MISSING = '@komigen/mobile-frontend/translation/TEXT_KEYS_MISSING' as const;

const createEqualSelector = createSelectorCreator(defaultMemoize, {
	resultEqualityCheck: isEqual
});

const REDUCER_NAME = 'translations';
const EMPTY_OBJECT = Object.freeze({});

export const selectTranslations = (state: RootState) => state[REDUCER_NAME] || EMPTY_OBJECT;

export const createTranslationSliceSelector = () =>
	createEqualSelector(
		selectTranslations,
		(_: Translation, textKeys: string[]) => textKeys,
		(allTranslations: Translation, textKeys) =>
			Object.fromEntries(Object.entries(allTranslations).filter(([k]) => textKeys.includes(k)))
	);

const ALL_REQUESTED = new Set(Object.keys(selectTranslations(preloadedState as PreloadedState)));

type FetchTranslationStartAction = {
	type: typeof FETCH_TRANSLATION_START;
};
type FetchTranslationSuccessAction = {
	type: typeof FETCH_TRANSLATION_SUCCESS;
	payload: Translation;
};
type TextKeysMissingAction = {
	type: typeof TEXT_KEYS_MISSING;
};

type SetTranslationAction = {
	type: typeof SET_TRANSLATION;
	value: Translation;
};

export type TextKeyLookupActions = FetchTranslationStartAction | FetchTranslationSuccessAction | TextKeysMissingAction;

export type TranslationDuckActions =
	| FetchTranslationStartAction
	| FetchTranslationSuccessAction
	| TextKeysMissingAction
	| SetTranslationAction;

type ApiResponse = {
	result: Translation;
};

export const textKeyLookup =
	(textkey: string[] = [], forceUpdate = false): ThunkAction<TextKeyLookupActions, unknown, AnyAction> =>
	async dispatch => {
		const notPreviouslyRequested = forceUpdate ? textkey : textkey.filter(key => !ALL_REQUESTED.has(key));

		if (notPreviouslyRequested.length > 0) {
			notPreviouslyRequested.forEach(key => ALL_REQUESTED.add(key));
			dispatch({
				type: FETCH_TRANSLATION_START
			});

			try {
				const { result } = (await requestApi('/textKey', notPreviouslyRequested, 'POST')) as ApiResponse; // Todo - remove casting when requestApi is refectored to Typescript

				dispatch({
					type: FETCH_TRANSLATION_SUCCESS,
					payload: { ...result }
				});
				return;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
			}

			notPreviouslyRequested.forEach(key => ALL_REQUESTED.delete(key));

			return dispatch({
				type: TEXT_KEYS_MISSING
			});
		}

		// Return null to satisy eslint rule
		return null;
	};

export const refresh = (): ThunkAction<TextKeyLookupActions, unknown, AnyAction> => (dispatch, getState) => {
	const state = getState();
	const textKeys = Object.keys(state.translations || {});
	return dispatch(textKeyLookup(textKeys, true));
};

export type InitialState = Record<string, never>; // Representation of an empty object

// Todo Disable this rule globally
// eslint-disable-next-line @typescript-eslint/default-param-last
function reducer(state: InitialState = {}, action: TranslationDuckActions) {
	switch (action.type) {
		case FETCH_TRANSLATION_SUCCESS:
			return {
				...state,
				...action.payload
			};
		case SET_TRANSLATION:
			return {
				...state,
				...action.value
			};
		default:
			return state;
	}
}

export type TranslationState = ReturnType<typeof reducer>;

export default reducer;
